import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Filter, Search, X } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';

import { addToCart, imgUrl } from '../data/mockData';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

import { toast } from 'sonner';


const API = process.env.REACT_APP_BACKEND_URL;


const CATEGORY_ORDER = [
  'Fustane',
  'Bluza',
  'Këmisha',
  'Pantallona të Shkurtra',
  'Pantallona të Gjata',
  'Xhaketa',
  'Xhupa',
  'Këpucë',
  'Kapele',
  'Çorape',
  'Të Brendshme',
  'Veshje e Përditshme',
  'Sportive',
  'Aksesorë',
  'Parfume',
];


const Collections = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchParams, setSearchParams] = useSearchParams();

  const searchQuery =
    searchParams.get('search') || '';

  const saleOnly =
    searchParams.get('sale') === 'true';

  // Filtrat kryesorë lexohen direkt nga URL-ja.
  // Kjo garanton që linket nga Home si:
  // /collections?gender=Meshkuj
  // /collections?gender=Femra
  // /collections?category=Aksesorë
  // /collections?category=Këpucë
  // aktivizojnë menjëherë filtrin përkatës.
  const selectedGender =
    searchParams.get('gender') || 'Të gjitha';

  const selectedSeason =
    searchParams.get('season') || 'Të gjitha';

  const selectedCategory =
    searchParams.get('category') || 'Të gjitha';

  const selectedCategories =
    (searchParams.get('categories') || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

  const selectedFilter =
    searchParams.get('filter') || 'all';


  // ============================================================
  // PAGINATION
  // ============================================================

  const PRODUCTS_PER_PAGE = 30;

  const requestedPage =
    parseInt(
      searchParams.get('page') || '1',
      10
    );


  const genders = [
    'Të gjitha',
    'Femra',
    'Meshkuj',
  ];

  const seasons = [
    'Të gjitha',
    'Verë',
    'Dimër',
  ];


  const updateUrlFilter = (key, value, allValue = 'Të gjitha') => {
    const next = new URLSearchParams(searchParams);

    if (key === 'category') {
      next.delete('categories');
    }

    // Kur ndryshohet filtër, kthehemi në faqen 1.
    next.delete('page');

    if (!value || value === allValue || value === 'all') {
      next.delete(key);
    } else {
      next.set(key, value);
    }

    setSearchParams(next);
  };


  const normalize = (value) =>
    String(value || '')
      .trim()
      .toLocaleLowerCase('sq-AL');


  // ============================================================
  // LOAD PRODUCTS
  // ============================================================

  const loadProducts = async () => {
    try {
      const res = await axios.get(
        `${API}/api/products`,
        {
          params: {
            _t: Date.now()
          }
        }
      );

      setProducts(
        Array.isArray(res.data)
          ? res.data
          : []
      );
    } catch (error) {
      console.error(
        'Gabim gjatë ngarkimit të produkteve:',
        error
      );

      toast.error(
        'Nuk u ngarkuan produktet. Provoni përsëri.',
        { duration: 3000 }
      );
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadProducts();

    const refreshProducts = () => {
      loadProducts();
    };

    window.addEventListener(
      'focus',
      refreshProducts
    );

    window.addEventListener(
      'pageshow',
      refreshProducts
    );

    return () => {
      window.removeEventListener(
        'focus',
        refreshProducts
      );

      window.removeEventListener(
        'pageshow',
        refreshProducts
      );
    };
  }, []);


  // ============================================================
  // CATEGORIES
  // ============================================================

  const availableCategories = [
    ...new Set(
      products
        .flatMap(
          product => {
            const productCategories =
              Array.isArray(product.categories)
              && product.categories.length
                ? product.categories
                : (
                    product.category
                      ? [product.category]
                      : []
                  );

            return productCategories;
          }
        )
        .filter(Boolean)
    ),
  ];


  // Kategoritë bazë shfaqen gjithmonë, edhe kur për momentin
  // nuk ka produkt në njërën prej tyre. Kategoritë e reja që
  // mund të krijohen nga Admin shtohen automatikisht në fund.
  const categories = [
    'Të gjitha',
    'Produkte me Ulje',
    ...CATEGORY_ORDER,
    ...availableCategories.filter(
      (category) =>
        !CATEGORY_ORDER.some(
          (known) =>
            normalize(known) === normalize(category)
        )
    ),
  ].filter(
    (category, index, array) =>
      array.findIndex(
        (item) =>
          normalize(item) === normalize(category)
      ) === index
  );


  // ============================================================
  // FILTER PRODUCTS
  // ============================================================

  const filteredProducts =
    products.filter((product) => {

      const genderMatch =
        selectedGender === 'Të gjitha'
        ||
        normalize(product.gender) === normalize(selectedGender);


      const seasonMatch =
        selectedSeason === 'Të gjitha'
        ||
        normalize(product.season) === normalize(selectedSeason)
        ||
        normalize(product.season) === normalize('Të gjitha');


      const hasSale =
        product.salePrice != null
        &&
        Number(product.salePrice) > 0
        &&
        Number(product.salePrice) < Number(product.price);


      const productCategories =
        Array.isArray(product.categories)
        && product.categories.length
          ? product.categories
          : (
              product.category
                ? [product.category]
                : []
            );


      let categoryMatch = true;

      if (selectedCategories.length > 0) {

        categoryMatch =
          selectedCategories.some(
            selected =>
              productCategories.some(
                productCategory =>
                  normalize(productCategory)
                  === normalize(selected)
              )
          );
      }

      else if (
        selectedCategory === 'Produkte me Ulje'
      ) {

        categoryMatch = hasSale;
      }

      else {

        categoryMatch =
          selectedCategory === 'Të gjitha'
          ||
          productCategories.some(
            productCategory =>
              normalize(productCategory)
              === normalize(selectedCategory)
          );
      }


      const q =
        searchQuery
          .trim()
          .toLowerCase();


      const variantColors =
        Array.isArray(product.variants)
          ? product.variants
              .map((variant) => variant.color || '')
              .join(' ')
              .toLowerCase()
          : '';


      const searchMatch =
        !q
        ||
        (product.name || '')
          .toLowerCase()
          .includes(q)
        ||
        (
          Array.isArray(product.categories)
          && product.categories.length
            ? product.categories.join(' ')
            : (product.category || '')
        )
          .toLowerCase()
          .includes(q)
        ||
        (product.description || '')
          .toLowerCase()
          .includes(q)
        ||
        variantColors.includes(q);


      let filterMatch = true;

      if (selectedFilter === 'new') {
        filterMatch = product.newArrival;
      }

      else if (
        selectedFilter === 'bestseller'
      ) {
        filterMatch = product.bestSeller;
      }


      const saleMatch =
        !saleOnly
        ||
        hasSale;


      return (
        genderMatch
        &&
        seasonMatch
        &&
        categoryMatch
        &&
        searchMatch
        &&
        filterMatch
        &&
        saleMatch
      );
    });


  const totalPages =
    Math.max(
      1,
      Math.ceil(
        filteredProducts.length
        /
        PRODUCTS_PER_PAGE
      )
    );


  const currentPage =
    Number.isNaN(requestedPage)
      ? 1
      : Math.min(
          Math.max(requestedPage, 1),
          totalPages
        );


  const pageStart =
    (currentPage - 1)
    *
    PRODUCTS_PER_PAGE;


  const paginatedProducts =
    filteredProducts.slice(
      pageStart,
      pageStart + PRODUCTS_PER_PAGE
    );


  useEffect(() => {
    if (
      requestedPage > totalPages
      &&
      totalPages >= 1
    ) {
      const next =
        new URLSearchParams(
          searchParams
        );

      if (totalPages <= 1) {
        next.delete('page');
      } else {
        next.set(
          'page',
          String(totalPages)
        );
      }

      setSearchParams(
        next,
        {
          replace: true
        }
      );
    }
  }, [
    requestedPage,
    totalPages,
    searchParams,
    setSearchParams
  ]);


  const goToPage = page => {
    const safePage =
      Math.min(
        Math.max(page, 1),
        totalPages
      );

    const next =
      new URLSearchParams(
        searchParams
      );

    if (safePage <= 1) {
      next.delete('page');
    } else {
      next.set(
        'page',
        String(safePage)
      );
    }

    setSearchParams(next);

    requestAnimationFrame(
      () => {
        document
          .getElementById('products-grid')
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
      }
    );
  };


  // ============================================================
  // ADD TO CART
  // ============================================================

  const handleAddToCart = (product) => {
    addToCart(product);

    window.dispatchEvent(
      new Event('cartUpdated')
    );

    toast.success(
      `${product.name} u shtua në shportë!`
      
    );
  };


  // ============================================================
  // COLLECTIONS SEARCH
  // ============================================================

  const updateCollectionSearch = value => {
    const next = new URLSearchParams(searchParams);

    next.delete('page');

    if (value.trim()) {
      next.set('search', value);
    } else {
      next.delete('search');
    }

    setSearchParams(next);
  };


  // ============================================================
  // CLEAR FILTERS
  // ============================================================

  const clearAllFilters = () => {
    setSearchParams({});
  };


  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Koleksioni"
        description="Zbuloni koleksionin Lor95 Boutique: veshje për femra dhe meshkuj, këpucë, veshje sportive, aksesorë dhe parfume. Porosit online në Shqipëri."
        path="/collections"
      />

      {/* ======================================================
          HERO
      ======================================================= */}

      <section className="relative h-80 flex items-center justify-center overflow-hidden bg-gray-900">

        <div className="absolute inset-0 z-0 opacity-30">

          <img
            src="https://images.unsplash.com/photo-1546333456-3e8ed81f41e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBib3V0aXF1ZXxlbnwwfHx8fDE3NzEyMzIzNTZ8MA&ixlib=rb-4.1.0&q=85"
            alt="Collections"
            className="w-full h-full object-cover"
          />

        </div>


        <div className="relative z-10 text-center px-4">

          <h1 className="text-5xl sm:text-6xl font-serif font-bold text-white mb-4 animate-in fade-in slide-in-from-bottom duration-700">
            Koleksionet Tona
          </h1>

          <p className="text-xl text-white/90 animate-in fade-in slide-in-from-bottom duration-700 delay-150">
            Zbuloni modë që ju përcakton
          </p>

        </div>

      </section>


      {/* ======================================================
          FILTERS
      ======================================================= */}

      <section className="py-12 bg-gray-50 border-b border-gray-200">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="space-y-6">

            {/* ACTIVE SEARCH */}

            {searchQuery && (

              <div className="flex items-center gap-3">

                <span className="text-sm text-gray-600">
                  Rezultatet e kërkimit për:
                </span>


                <span
                  data-testid="active-search-badge"
                  className="inline-flex items-center gap-2 bg-gray-900 text-white text-sm font-medium px-4 py-1.5"
                >

                  <Search className="h-3.5 w-3.5" />

                  "{searchQuery}"


                  <button
                    data-testid="clear-search-btn"
                    onClick={() => {
                      const next = new URLSearchParams(searchParams);
                      next.delete('search');
                      setSearchParams(next);
                    }}
                    className="hover:text-gray-300 ml-1"
                    aria-label="Pastro kërkimin"
                  >
                    <X className="h-4 w-4" />
                  </button>

                </span>

              </div>

            )}


            {/* GENDER */}

            <div>

              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Gjinia
              </h3>


              <div className="flex flex-wrap gap-2">

                {genders.map((gender) => (

                  <Button
                    key={gender}
                    data-testid={`filter-gender-${gender}`}
                    onClick={() => updateUrlFilter('gender', gender)}
                    variant={
                      selectedGender === gender
                        ? 'default'
                        : 'outline'
                    }
                    className={`rounded-none ${
                      selectedGender === gender
                        ? 'bg-gray-900 text-white hover:bg-gray-800'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >

                    {gender}

                  </Button>

                ))}

              </div>

            </div>


            {/* SEASON */}

            <div>

              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Stina
              </h3>


              <div className="flex flex-wrap gap-2">

                {seasons.map((season) => (

                  <Button
                    key={season}
                    data-testid={`filter-season-${season}`}
                    onClick={() => updateUrlFilter('season', season)}
                    variant={
                      selectedSeason === season
                        ? 'default'
                        : 'outline'
                    }
                    className={`rounded-none ${
                      selectedSeason === season
                        ? 'bg-gray-900 text-white hover:bg-gray-800'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >

                    {season}

                  </Button>

                ))}

              </div>

            </div>


            {/* SEARCH PRODUCTS */}

            <div>

              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Kërko produkt
              </h3>

              <div className="relative max-w-2xl">

                <Search
                  className="
                    absolute
                    left-4
                    top-1/2
                    -translate-y-1/2
                    h-5
                    w-5
                    text-gray-400
                  "
                />

                <Input
                  type="search"
                  value={searchQuery}
                  onChange={
                    event =>
                      updateCollectionSearch(
                        event.target.value
                      )
                  }
                  placeholder="Kërko p.sh. atlete, bluzë, pantallona..."
                  className="
                    h-12
                    rounded-none
                    pl-12
                    pr-11
                    border-gray-300
                    bg-white
                    text-base
                  "
                  aria-label="Kërko produkte"
                />

                {searchQuery && (

                  <button
                    type="button"
                    onClick={
                      () =>
                        updateCollectionSearch('')
                    }
                    className="
                      absolute
                      right-3
                      top-1/2
                      -translate-y-1/2
                      p-1
                      text-gray-500
                      hover:text-gray-900
                    "
                    aria-label="Pastro kërkimin"
                  >

                    <X className="h-5 w-5" />

                  </button>

                )}

              </div>

              <p className="text-xs text-gray-500 mt-2">
                Kërkimi punon bashkë me kategorinë, gjininë dhe sezonin e zgjedhur.
              </p>

            </div>


            {/* CATEGORY */}

            <div>

              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Kategoria
              </h3>


              <div className="flex flex-wrap gap-2">

                {categories.map((category) => {

                  const isCombinedActive =
                    selectedCategories.some(
                      (selected) =>
                        normalize(selected)
                        === normalize(category)
                    );

                  const isActive =
                    isCombinedActive
                    ||
                    (
                      selectedCategories.length === 0
                      &&
                      selectedCategory === category
                    );

                  return (
                    <Button
                      key={category}
                      data-testid={`filter-category-${category}`}
                      onClick={() => updateUrlFilter('category', category)}
                      variant={
                        isActive
                          ? 'default'
                          : 'outline'
                      }
                      className={`whitespace-nowrap rounded-none ${
                        isActive
                          ? 'bg-gray-900 text-white hover:bg-gray-800'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                      }`}
                    >

                      {category}

                    </Button>
                  );
                })}

              </div>

            </div>


            {/* TAG FILTER */}

            <div className="flex items-center justify-between pt-4 border-t border-gray-200">

              <div className="flex items-center space-x-3">

                <Filter className="h-5 w-5 text-gray-600" />


                <Select
                  value={selectedFilter}
                  onValueChange={(value) => updateUrlFilter('filter', value, 'all')}
                >

                  <SelectTrigger
                    data-testid="filter-tag-select"
                    className="w-48 rounded-none"
                  >

                    <SelectValue placeholder="Filtro sipas" />

                  </SelectTrigger>


                  <SelectContent>

                    <SelectItem value="all">
                      Të Gjitha Produktet
                    </SelectItem>

                    <SelectItem value="new">
                      Ardhje të Reja
                    </SelectItem>

                    <SelectItem value="bestseller">
                      Më të Shitura
                    </SelectItem>

                  </SelectContent>

                </Select>

              </div>


              <div
                data-testid="products-count"
                className="text-sm text-gray-600"
              >

                Duke shfaqur{' '}
                {filteredProducts.length === 0 ? 0 : pageStart + 1}
                {'–'}
                {Math.min(
                  pageStart + PRODUCTS_PER_PAGE,
                  filteredProducts.length
                )}
                {' nga '}
                {filteredProducts.length}
                {' '}
                {
                  filteredProducts.length === 1
                    ? 'produkt'
                    : 'produkte'
                }

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* ======================================================
          PRODUCTS GRID
      ======================================================= */}

      <section id="products-grid" className="py-16 bg-white scroll-mt-24">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {loading ? (

            <div className="text-center py-20">

              <p className="text-xl text-gray-600">
                Duke ngarkuar produktet...
              </p>

            </div>

          ) : filteredProducts.length === 0 ? (

            <div className="text-center py-20">

              <p className="text-xl text-gray-600">
                Nuk u gjetën produkte që përputhen me filtrat tuaj.
              </p>


              <Button
                data-testid="clear-filters-btn"
                onClick={clearAllFilters}
                className="mt-6 bg-gray-900 hover:bg-gray-800 text-white rounded-none"
              >

                Pastro Filtrat

              </Button>

            </div>

          ) : (

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">

              {paginatedProducts.map(
                (product, index) => {

                  // =================================================
                  // VARIANTS
                  // =================================================

                  const hasVariants =
                    Array.isArray(product.variants)
                    &&
                    product.variants.length > 0;


                  const colors =
                    hasVariants
                      ? [
                          ...new Set(
                            product.variants
                              .map(
                                (variant) =>
                                  variant.color
                              )
                              .filter(Boolean)
                          ),
                        ]
                      : [];


                  // =================================================
                  // MAIN IMAGE
                  // =================================================

                  const mainImage =
                    product.image
                    ||
                    product.images?.[0]?.url
                    ||
                    '';


                  // =================================================
                  // TOTAL STOCK
                  // =================================================

                  const totalStock =
                    typeof product.stock === 'number'
                      ? product.stock
                      : hasVariants
                        ? product.variants.reduce(
                            (sum, variant) =>
                              sum
                              +
                              (
                                parseInt(
                                  variant.stock,
                                  10
                                ) || 0
                              ),
                            0
                          )
                        : 0;


                  // =================================================
                  // SALE
                  // =================================================

                  const hasSale =
                    product.salePrice != null
                    &&
                    Number(product.salePrice) > 0
                    &&
                    Number(product.salePrice) < Number(product.price);


                  const discountPercent =
                    hasSale
                      ? Math.round(
                          (
                            (
                              Number(product.price)
                              -
                              Number(product.salePrice)
                            )
                            /
                            Number(product.price)
                          )
                          *
                          100
                        )
                      : 0;


                  return (

                    <Card
                      key={product.id}
                      data-testid={`product-card-${product.id}`}
                      className="group overflow-hidden border-none shadow-md hover:shadow-2xl transition-all duration-500 animate-in fade-in slide-in-from-bottom"
                      style={{
                        animationDelay:
                          `${index * 50}ms`,
                      }}
                    >

                      <CardContent className="p-0">

                        {/* IMAGE */}

                        <div className="relative overflow-hidden aspect-[3/4]">

                          <Link
                            to={`/product/${product.id}`}
                            data-testid={`product-link-${product.id}`}
                          >

                            <img
                              src={imgUrl(mainImage)}
                              alt={product.name}
                              loading="lazy"
                              decoding="async"
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            />

                          </Link>


                          {/* BADGES */}

                          <div className="absolute top-4 left-4 flex flex-col gap-2">

                            {hasSale && (

                              <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 shadow-sm">
                                -{discountPercent}%
                              </span>

                            )}


                            {product.newArrival && (

                              <span className="bg-white text-gray-900 text-xs font-bold px-3 py-1">
                                E RE
                              </span>

                            )}


                            {product.bestSeller && (

                              <span className="bg-gray-900 text-white text-xs font-bold px-3 py-1">
                                MË E SHITUR
                              </span>

                            )}


                            {totalStock <= 0 && (

                              <span className="bg-red-600 text-white text-xs font-bold px-3 py-1">
                                E SHITUR
                              </span>

                            )}

                          </div>


                          {/* HOVER */}

                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">

                            <Button
                              data-testid={`add-to-cart-${product.id}`}
                              disabled={totalStock <= 0}
                              onClick={() => {

                                if (totalStock <= 0) {
                                  return;
                                }


                                if (
                                  hasVariants
                                  ||
                                  product.sizes?.length > 0
                                ) {

                                  navigate(
                                    `/product/${product.id}`
                                  );

                                } else {

                                  handleAddToCart(
                                    product
                                  );
                                }
                              }}
                              className="bg-white text-gray-900 hover:bg-gray-100 font-semibold rounded-none transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500 disabled:opacity-60"
                            >

                              <ShoppingCart className="mr-2 h-4 w-4" />


                              {
                                totalStock <= 0
                                  ? 'E Shitur'

                                  : hasVariants
                                    ? 'Zgjidh Variantin'

                                    : product.sizes?.length > 0
                                      ? 'Zgjidh Masën'

                                      : 'Shto në Shportë'
                              }

                            </Button>

                          </div>

                        </div>


                        {/* CONTENT */}

                        <div className="p-5 space-y-2">

                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">

                            {product.gender}
                            {' • '}
                            {product.season}
                            {' • '}
                            {product.category}

                          </p>


                          <Link
                            to={`/product/${product.id}`}
                          >

                            <h3 className="text-lg font-semibold text-gray-900 line-clamp-1 hover:underline underline-offset-4">

                              {product.name}

                            </h3>

                          </Link>


                          <p className="text-sm text-gray-600 line-clamp-2">

                            {product.description}

                          </p>


                          {/* COLORS */}

                          {colors.length > 0 && (

                            <div className="pt-1">

                              <p className="text-xs font-medium text-gray-500">

                                Ngjyrat:{' '}

                                <span className="text-gray-700">

                                  {colors.join(', ')}

                                </span>

                              </p>

                            </div>

                          )}


                          {/* PRICE + STOCK */}

                          <div className="flex items-center justify-between gap-3 pt-2">

                            <div>
                              {(
                                product.salePrice != null
                                &&
                                Number(product.salePrice) > 0
                                &&
                                Number(product.salePrice) < Number(product.price)
                              ) ? (
                                <div className="flex items-baseline gap-2">
                                  <span className="text-sm text-gray-400 line-through">
                                    {(product.price / 100).toFixed(0)} Lek
                                  </span>
                                  <span className="text-2xl font-bold text-red-600">
                                    {(product.salePrice / 100).toFixed(0)} Lek
                                  </span>
                                </div>
                              ) : (
                                <p className="text-2xl font-bold text-gray-900">
                                  {(product.price / 100).toFixed(0)} Lek
                                </p>
                              )}
                            </div>


                            <span
                              className={`
                                text-xs
                                font-semibold
                                px-2
                                py-1
                                whitespace-nowrap

                                ${
                                  totalStock <= 0

                                    ? 'bg-red-100 text-red-700'

                                    : totalStock <= 3

                                      ? 'bg-amber-100 text-amber-700'

                                      : 'bg-green-100 text-green-700'
                                }
                              `}
                            >

                              Stok:
                              {' '}
                              {totalStock}

                            </span>

                          </div>


                          {/* VARIANT INFO */}

                          {hasVariants && (

                            <p className="text-xs text-gray-400 pt-1">

                              {
                                colors.length
                              }
                              {' '}
                              {
                                colors.length === 1
                                  ? 'ngjyrë'
                                  : 'ngjyra'
                              }

                              {' • '}

                              {
                                [
                                  ...new Set(
                                    product.variants
                                      .map(
                                        variant =>
                                          variant.size
                                      )
                                      .filter(Boolean)
                                  ),
                                ].length
                              }
                              {' '}
                              masa

                            </p>

                          )}

                        </div>

                      </CardContent>

                    </Card>

                  );
                }
              )}

            </div>

          )}


          {!loading && filteredProducts.length > 0 && totalPages > 1 && (

            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-3">

              <Button
                type="button"
                variant="outline"
                disabled={currentPage === 1}
                onClick={() => goToPage(currentPage - 1)}
                className="rounded-none min-w-[120px]"
              >
                ← Mëparshme
              </Button>


              <div className="flex items-center gap-2 max-w-full overflow-x-auto px-1 py-1">

                {Array.from(
                  { length: totalPages },
                  (_, index) => index + 1
                ).map(page => (

                  <Button
                    key={page}
                    type="button"
                    variant={
                      currentPage === page
                        ? 'default'
                        : 'outline'
                    }
                    onClick={() => goToPage(page)}
                    className={`rounded-none min-w-10 ${
                      currentPage === page
                        ? 'bg-gray-900 text-white hover:bg-gray-800'
                        : ''
                    }`}
                  >
                    {page}
                  </Button>

                ))}

              </div>


              <Button
                type="button"
                variant="outline"
                disabled={currentPage === totalPages}
                onClick={() => goToPage(currentPage + 1)}
                className="rounded-none min-w-[120px]"
              >
                Tjetër →
              </Button>

            </div>
          )}


          {!loading && filteredProducts.length > 0 && (

            <p className="mt-4 text-center text-xs text-gray-500">
              Faqja {currentPage} nga {totalPages}
            </p>

          )}

        </div>

      </section>


      {/* ======================================================
          CTA
      ======================================================= */}

      <section className="py-16 bg-gray-50">

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">

          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4">

            Keni Nevojë për Ndihmë për të Gjetur Stilin Tuaj të Përkryer?

          </h2>


          <p className="text-lg text-gray-600 mb-8">

            Vizitoni butikun tonë për ndihmë të personalizuar me stilin

          </p>


          <Button
            asChild
            size="lg"
            className="bg-gray-900 hover:bg-gray-800 text-white px-8 rounded-none"
          >

            <Link to="/visit">

              Vizitoni Butikun Tonë

            </Link>

          </Button>

        </div>

      </section>

    </div>
  );
};


export default Collections;